import React, { useState, useCallback, useEffect, useRef, createRef, useMemo } from "react";
import {
    Grid,
    GridColumn,
    GridNoRecords,
    getSelectedState,
    getSelectedStateFromKeyDown,
} from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";
import { Splitter } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { toast } from "react-toastify";
import styles from "../Casefile.module.scss";
import activityStyles from "components/Activities/CaseActivity.module.scss";
import { formatDateForAPI } from "components/common/formatter.js";
import {
    getDocument,
    getDocumentEditor,
    getMatterDocDuplicate,
    copyMatterActivity,
    getSingleMatterActivity,
    updateMatterActivity,
    saveDocumentEditor,
    downloadDocumentAsPDF,
    downloadDocument,
    downloadDocumentsAsZip,
    getOutlookWebURLViewEmail,
} from "api/index";
import { getBillingRequestDetails } from "services/billingRequestsService";

import { getAuthTicket } from "services/authServerService";

import CaseActivityForm from "components/Activities/CaseActivityForm";
import DocumentViewer from "components/common/DocumentViewer";
import { fileIcon } from "components/common/fileIcon";
import { useDoubleClick } from "components/common/hooks/SingleDoubleClick";
import {
    excelExt,
    wordExt,
    getFileExtension,
} from "components/common/DocumentViewer/fileUtils";

import ShareDocumentModal from "../ShareDocumentModal";
import { HandleGridBody } from "components/common/HandleGridBody";
import FilterComponent from "components/CaseFiles/FilterComponent";
import DocumentEditor from "components/common/EditDocumentDialog";

import ConfirmationDialog from "components/common/ConfirmationDialog";
import ESigning from "../ESigning";
import MenuCell from "components/Activities/MenuCell";
import MoveCopyActivityModal from "components/Activities/MoveCopyActivityModal";
import { FileDragDrop } from "components/Activities/FileDragDrop";
import {
    ACTIVITY_DATA_ITEM_KEY,
    ACTIVITY_SELECTED_FIELD,
    applyActivitiesDragTransfer,
    buildMappedGridData,
    downloadBlobAsFile,
    getActivitiesToDelete,
    getActivitySubtypeStatus,
    getActivityTagChipStyle,
    getActivityTagId,
    getDeleteConfirmationMessage,
    getDeleteSuccessMessage,
    mergeDuplicateActivityData,
    resolveDuplicateActivityId,
    getRowStyle,
    getSelectedDocumentIds,
    getSelectedDocumentsForShare,
    getStatusClass,
    isEmailFile,
    isOfficeFile,
    parseDownloadFilename,
    resolveEmailActivityType,
    truncateFileName,
} from "components/Activities/activityUtils";
import { checkEmailAttached } from "api/index";
import EmailAlreadyAttachedDialog from "components/Activities/EmailAlreadyAttachedDialog";
import { useCompany } from "services/companyService";
import { useCurrentUser } from "services/currentUserService";
import { getActiveActivityTypes } from "services/activityTypeService";
import { useMatterActivityTags } from "services/activityTagService";
import { GridLoader } from "components/GridLoader";
import ChitRequest from "pages/ChitRequest/ChitRequest";
import BillPostingModal from "pages/Bills/BillRequestModal.jsx";
import { isNonEmptyBillResourceId } from "pages/Bills/billReportResourceId";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";
import { MdOutlineBlock } from "react-icons/md";
import { useDispatch } from "react-redux";
import { clearMattersBalances } from "actions/matters.action";
import {
    useCurrentUserSettings,
    updateCurrentUserSettings,
} from "services/userSettingsService";
import { useApplicationSettings } from "services/applicationSettingsService";
import { useSetting } from "components/common/settingsService";
import GridColumnsChooser from "components/common/grid/GridColumnsChooser";
import useGridColumnSettings from "components/common/grid/useGridColumnSettings";

const DEFAULT_PANES = [
    {
        size: "100%",
        max: "70%",
        min: "450px",
        collapsible: true,
    },
    {},
];


const USER_SETTING_DOC_VIEWER_WIDTH_KEY = "CaseActivitiesDocViewerWidth";
const DEFAULT_COLUMNS = [
    { field: "type", title: "Type", width: 120, visible: true, order: 0 },
    { field: "date", title: "Date", width: 100, visible: true, order: 1 },
    { field: "feeEarnerRef", title: "FE", width: 60, visible: true, order: 2 },
    { field: "details", title: "Details", visible: true, order: 3 },
    { field: "document", title: "Document", width: 110, visible: true, order: 4 },
    { field: "email", title: "Email", width: 260, visible: true, order: 5 },
    { field: "emailFrom", title: "Email From", width: 200, visible: false, order: 6 },
    { field: "emailTo", title: "Email To", width: 200, visible: false, order: 7 },
];

const CaseActivitiesPage = (props) => {
    const idGetter = getter(ACTIVITY_DATA_ITEM_KEY);
    const dispatch = useDispatch();
    const { data: userSettings } = useCurrentUserSettings();
    const { data: currentUser } = useCurrentUser();
    const { data: applicationSettings } = useApplicationSettings();

    const getDocViewerWidth = () => {
        const savedWidth = userSettings?.find(
            (s) => s.key === USER_SETTING_DOC_VIEWER_WIDTH_KEY,
        )?.value;
        return savedWidth || "40%";
    };

    const getDynamicDocOpenPanes = () => [
        {
            collapsible: true,
        },
        {
            size: getDocViewerWidth(),
            min: "500px",
            collapsible: false,
        },
    ];

    const uploadRef = createRef();
    const [eventDataState, setEventDataState] = useState();
    const [filterFeeEarner, setFillterFeeEarner] = useState();
    const [deleteConfirmation, setDeleteConfirmation] = useState(false);
    const [editDocumentDialog, setEditDocumentDialog] = useState();
    const [eSigning, setESigning] = useState();
    const [activityTypes, setActivityTypes] = useState([]);
    const [droppedFilesActivity, setDroppedFilesActivity] = useState([]);
    const [selectedState, setSelectedState] = useState({});
    const [paneDocument, setPaneDocument] = useState([]);
    const [panes, setPanes] = useState(DEFAULT_PANES);
    const [loader, setLoader] = useState(true);
    const [showShareDocument, setShowShareDocument] = useState(false);
    const [currentDoc, setCurrentDoc] = useState({});
    const [isRefreshed, setIsRefreshed] = useState(false);
    const [updateFilter, setUpdateFilter] = useState(false);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const selectedTypeIds = useMemo(
        () =>
            selectedTypes
                .map((t) => t.activityTypeID ?? t.value ?? t.id)
                .filter((id) => id != null && id !== ""),
        [selectedTypes]
    );
    const [selectedTags, setSelectedTags] = useState([]);
    const selectedTagIds = useMemo(
        () => selectedTags.map((t) => getActivityTagId(t)),
        [selectedTags]
    );
    const { data: tagsData, loading: tagsLoading, fetched: tagsFetched, refresh: refreshMatterTags } = useMatterActivityTags(props.matterId);
    const [multiSel, setMultiSel] = useState(false);
    const [chitRequestModal, setChitRequestModal] = useState(false);
    const [billRequestModal, setBillRequestModal] = useState(false);
    const [billingRequestPayload, setBillingRequestPayload] = useState(null);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [prefilledChitDocs, setPrefilledChitDocs] = useState([]);
    const [openMenuActivityId, setOpenMenuActivityId] = useState(null);
    const [emailAttachedPrompt, setEmailAttachedPrompt] = useState(null);
    const [droppedTypesByFile, setDroppedTypesByFile] = useState({});
    const [droppedEmailDate, setDroppedEmailDate] = useState(null);
    const [droppedEmailDatesByFile, setDroppedEmailDatesByFile] = useState({});
    const [droppedEmailSubjectsByFile, setDroppedEmailSubjectsByFile] = useState({});
    let date = new Date();

    const [toDateSelector, setToDateSelector] = useState(date);
    const { data: company } = useCompany();
    const locale = company?.locale;

    let yearsStartDate = new Date(company.yearStartDate);

    const [fromDate, setFromDate] = useState(formatDateForAPI(yearsStartDate));

    const [toDate, setToDate] = useState(formatDateForAPI(date));
    const [fromDateSelector, setFromDateSelector] = useState(yearsStartDate);

    const [list, setLists] = useState({
        data: [],
        total: 20,
    });
    const [filterParameters, setFilterParmeters] = useState({
        postingStatus: "false",
    });

    const [valueFrom, setValueFrom] = useState(false);
    const [valueTo, setValueTo] = useState(false);
    const createState = (skip, take) => {
        let pagerSettings = {
            pageSizes: GRID_PAGE_SIZES,
        };
        return {
            pageSize: take,
            pageable: pagerSettings,
        };
    };

    const [state, setState] = useState(createState(0, 200));
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const [isOverGrid, setIsOverGrid] = useState(false);
    const activityGridRef = useRef(null);
    const [viewActivity, setViewActivity] = useState();
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [moveCopyModal, setMoveCopyModal] = useState(null);
    const [moveCopyProcessing, setMoveCopyProcessing] = useState(false);
    const [copyMoveTargetId, setCopyMoveTargetId] = useState(null);
    const [copyMoveTargetText, setCopyMoveTargetText] = useState(null);
    const [moveSource, setMoveSource] = useState(null);
    const [isView, setView] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const [dataState, setDataState] = useState({
        take: 200,
        skip: 0,
        sort: [
            {
                field: "date",
                dir: "desc",
            },
        ],
    });
    const prevSearchValueRef = useRef(props.searchValue);

    const defaultEmailClient = useSetting("DefaultEmailClient", true);
    const defaultEditOfficeDocumentClient = useSetting("DefaultEditOfficeDocumentClient", true);
    const defaultDocumentClient = useSetting("DefaultDocumentClient", true);

    useEffect(() => {
        if (
            (prevSearchValueRef.current && !props.searchValue) ||
            (props.searchValue && props.searchValue !== prevSearchValueRef.current)
        ) {
            setLoader(true);
            // Reset selection when search changes
            setSelectedState({});
            setCurrentDoc({});
            setPaneDocument([]);
            setPanes(DEFAULT_PANES);
        }
        prevSearchValueRef.current = props.searchValue;
    }, [props.searchValue]);

    const saveWidthTimeoutRef = useRef(null);
    const detailsCellRef = useRef({});
    const filterAutoApplySkip = useRef(false);
    const {
        visibleColumns,
        renderKey: columnsRenderKey,
        gridProps: columnGridProps,
        chooserProps: columnChooserProps,
    } = useGridColumnSettings({
        gridKey: "CaseActivities",
        defaultColumns: DEFAULT_COLUMNS,
    });

    const columnsHeaderCell = useCallback(
        () => <GridColumnsChooser {...columnChooserProps} />,
        [columnChooserProps],
    );

    const getActivityType = async () => {
        const data = await getActiveActivityTypes();
        setActivityTypes(data.slice());
    };

    useEffect(() => {
        getActivityType();
    }, []);

    useEffect(() => {
        if (!props.refreshClicked) return;
        handleRefresh();
        props?.setRefreshClicked(false);
    }, [props?.refreshClicked]);

    const onOpen = () => { };

    useEffect(() => {
        if (!props?.filterClicked) return;

        const handlePointerDown = (event) => {
            const anchorElement = props?.filterAnchor?.current;
            if (anchorElement && anchorElement.contains(event.target)) {
                return;
            }
            if (event.target?.closest?.(".k-calendar")) {
                return;
            }

            if (event.target?.closest?.(".k-popup")) {
                return;
            }

            const popupElement = props?.filterContentRef?.current;
            const popupContainer = popupElement
                ? popupElement.closest(".k-animation-container")
                : null;

            const rect =
                popupContainer?.getBoundingClientRect?.() ||
                popupElement?.getBoundingClientRect?.();

            if (!rect) {
                props?.setFilterClicked(false);
                return;
            }

            const { clientX, clientY } = event;
            const isInside =
                clientX >= rect.left &&
                clientX <= rect.right &&
                clientY >= rect.top &&
                clientY <= rect.bottom;
            if (!isInside) {
                props?.setFilterClicked(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown, true);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown, true);
        };
    }, [
        props?.filterClicked,
        props?.setFilterClicked,
        props?.filterAnchor,
        props?.filterContentRef,
    ]);

    useEffect(() => {
        return () => {
            clearTimeout(saveWidthTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (updateFilter) {
            setUpdateFilter(false);
        }
    }, [updateFilter]);

    useEffect(() => {
        if (filterAutoApplySkip.current) {
            filterAutoApplySkip.current = false;
            return;
        }
        setUpdateFilter(true);
        const hasFilter = valueFrom || valueTo || selectedTypeIds.length > 0 || selectedTagIds.length > 0;
        props.setFilterApplied(hasFilter);
    }, [valueFrom, valueTo, fromDate, toDate, selectedTypeIds, selectedTagIds]);

    const onChange = (event) => {
        if (event?.newState[0].collapsed) {
            setPanes([
                {
                    ...event?.newState[0],
                },
                {},
            ]);
            return;
        } else if (!event?.newState[1].collapsed && !event?.newState[1].size) {
            setPanes(getDynamicDocOpenPanes());
            return;
        } else {
            setPanes([...event.newState]);
        }
        if (event.newState[1]?.size && paneDocument.length > 0) {
            clearTimeout(saveWidthTimeoutRef.current);
            saveWidthTimeoutRef.current = setTimeout(async () => {
                try {
                    await updateCurrentUserSettings([
                        {
                            key: USER_SETTING_DOC_VIEWER_WIDTH_KEY,
                            value: event.newState[1].size,
                        },
                    ]);
                } catch (error) {
                    console.error("Failed to save document viewer width", error);
                }
            }, 500);
        }
    };

    const proceedWithDroppedFiles = (
        files,
        typesByFile,
        emailDate,
        datesByFile,
        subjectsByFile,
    ) => {
        setDroppedTypesByFile(typesByFile ?? {});
        setDroppedEmailDate(emailDate ?? null);
        setDroppedEmailDatesByFile(datesByFile ?? {});
        setDroppedEmailSubjectsByFile(subjectsByFile ?? {});
        setDroppedFilesActivity([...files]);
        props.setAddActivity(true);
    };

    const handleDrop = async (event) => {
        if (!event || event.length === 0) {
            toast.error("No file found");
            return;
        }

        const files = Array.from(event);
        const companyDomain = company?.domains;

        let emailDate = null;
        const typesByFile = {};
        const datesByFile = {};
        const subjectsByFile = {};
        const attachedItems = [];

        try {
            for (const file of files) {
                if (!isEmailFile(file?.name)) continue;
                try {
                    const { data } = await checkEmailAttached(file);
                    const status = data?.data ?? data;
                    if (file?.name) {
                        typesByFile[file.name] = resolveEmailActivityType(
                            activityTypes,
                            status?.fromAddress,
                            companyDomain,
                        );
                    }
                    if (status?.date && file?.name) {
                        datesByFile[file.name] = status.date;
                    }
                    if (status?.subject && file?.name) {
                        subjectsByFile[file.name] = status.subject;
                    }
                    if (!emailDate) {
                        emailDate = status?.date ?? null;
                    }
                    if (status?.isAttached) {
                        const fallbackSubject =
                            file?.name?.replace(/\.(eml|msg)$/i, "") ||
                            "(No Subject)";
                        attachedItems.push({
                            subject: status?.subject ?? fallbackSubject,
                            matterRef: status?.matterRef,
                            matterDetails: status?.matterDetails,
                        });
                    }
                } catch (err) {
                    console.error("IsEmailAttached check failed", err);
                }
            }
        } catch (err) {
            console.error("Email duplicate scan failed", err);
        }

        if (attachedItems.length > 0) {
            setEmailAttachedPrompt({
                files,
                attachedItems,
                typesByFile,
                emailDate,
                datesByFile,
                subjectsByFile,
            });
            return;
        }

        proceedWithDroppedFiles(
            files,
            typesByFile,
            emailDate,
            datesByFile,
            subjectsByFile,
        );
    };

    const pickDocIdForSelection = (oldState, newState) => {
        for (const id of Object.keys(newState)) {
            if (newState[id] && !oldState?.[id]) {
                const row = list?.data?.find((item) => item.activityId == id);
                if (row?.documentId) return row.documentId;
            }
        }
        const selectedIds = Object.keys(newState || {}).filter((k) => newState[k]);
        for (let i = selectedIds.length - 1; i >= 0; i--) {
            const row = list?.data?.find((item) => item.activityId == selectedIds[i]);
            if (row?.documentId) return row.documentId;
        }
        return null;
    };

    const syncPaneToSelection = (oldState, newState) => {
        const docId = pickDocIdForSelection(oldState, newState);
        if (docId) {
            showPaneFile(docId);
        } else {
            setPaneDocument([]);
            setPanes(DEFAULT_PANES);
        }
    };

    const onSelectionChange = (event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: selectedState,
            dataItemKey: ACTIVITY_DATA_ITEM_KEY,
        });
        setMultiSel(true);
        setSelectedState(newSelectedState);
        syncPaneToSelection(selectedState, newSelectedState);
    };

    const onKeyDown = (event) => {
        if (event.nativeEvent.code === "Enter") {
            const newSelectedState = getSelectedStateFromKeyDown({
                event,
                selectedState: selectedState,
                dataItemKey: ACTIVITY_DATA_ITEM_KEY,
            });
            setSelectedState(newSelectedState);
            syncPaneToSelection(selectedState, newSelectedState);
        }
    };

    const handleTypeChange = (e) => {
        setSelectedTypes(e.value || []);
    };

    const onChangeFromDateSelect = (event) => {
        if (!valueFrom) {
            setValueFrom(true);
        }
        setFromDateSelector(event.value);
        let fromDate = null;
        let date = new Date(event.value);
        if (event.value) {
            fromDate = formatDateForAPI(date);
        }
        setFromDate(fromDate);
    };

    const onMatterRefClickActions = () => { };

    const onChangeToDateSelect = (event) => {
        if (!valueTo) {
            setValueTo(true);
        }
        setToDateSelector(event.value);
        let toDate = null;
        let date = new Date(event.value);
        if (event.value) {
            toDate = formatDateForAPI(date);
        }
        setToDate(toDate);
    };

    const updateFilterValues = () => {
        const { setFilterClicked, setFilterApplied } = props;
        setUpdateFilter(true);
        setFilterClicked(false);

        if (!valueFrom && !valueTo && selectedTypes.length === 0 && selectedTags.length === 0) {
            setFromDateSelector(yearsStartDate);
            setToDateSelector(date);
            setFilterApplied(false);
            return;
        }
        setFilterApplied(true);
    };

    const handleDeleteClick = (dataItem) => {
        setDeleteConfirmation(
            getActivitiesToDelete(dataItem, selectedState, list?.data),
        );
    };

    const deleteMatteractivity = async (items) => {
        const itemsArray = Array.isArray(items) ? items : [items];
        try {
            await Promise.all(
                itemsArray.map((data) =>
                    updateMatterActivity(props.matterId, data?.activityId, {
                        ...data,
                        active: false,
                    }),
                ),
            );
            setSelectedState({});
            toast.success(getDeleteSuccessMessage(itemsArray));
            getActivitiesData();
        } catch (error) { }

        //Close confirmation dialog on error as well.
        setDeleteConfirmation();
    };

    const handleDownload = async (documentId, asPDF = false, fromDocView) => {
        setLoader(true);
        try {
            const selectedDocumentIds = getSelectedDocumentIds(
                selectedState,
                list?.data,
            );

            let response;
            let fileName;
            if (selectedDocumentIds.length > 1 && !fromDocView) {
                fileName =
                    (props?.matterDetails?.matterRef?.replace(/\//g, "-") ||
                        "downloaded-folder") + ".zip";
                response = await downloadDocumentsAsZip(fileName, selectedDocumentIds);
            } else if (selectedDocumentIds.length === 1 || documentId) {
                response = asPDF
                    ? await downloadDocumentAsPDF(documentId || selectedDocumentIds[0])
                    : await downloadDocument(documentId || selectedDocumentIds[0]);

                fileName = parseDownloadFilename(response, asPDF);
            } else {
                setLoader(false);
                return;
            }

            downloadBlobAsFile(response.data, fileName);
        } catch (error) { } finally {
            setLoader(false);
        }
    };

    const handleDuplicate = async (matterId, sourceActivity) => {
        const activityId = resolveDuplicateActivityId(sourceActivity);
        if (!matterId || !activityId) return;

        try {
            const { data } = await getMatterDocDuplicate(matterId, activityId);
            const sourceRow =
                typeof sourceActivity === "object" ? sourceActivity : null;
            props.setAddActivity(true);
            setViewActivity(mergeDuplicateActivityData(data?.data, sourceRow));
            setView(true);
            setIsDuplicate(true);
        } catch (error) { }
    };

    const handleMoveCopyActivity = (activity) => {
        if (!activity) return;
        setMoveCopyModal({ sourceActivity: activity });
    };

    const handleMoveCopyConfirm = async (mode, targetMatter) => {
        const { sourceActivity } = moveCopyModal || {};
        const sourceMatterId = props?.matterId ?? sourceActivity?.matterId;
        const activityId = resolveDuplicateActivityId(sourceActivity);
        const targetMatterId = targetMatter?.matterId;
        if (!sourceMatterId || !activityId || !targetMatterId) return;

        setMoveCopyProcessing(true);
        try {
            const { data } = await copyMatterActivity(
                sourceMatterId,
                activityId,
                targetMatterId,
            );
            const sourceRow =
                typeof sourceActivity === "object" ? sourceActivity : null;
            setCopyMoveTargetId(targetMatterId);
            setCopyMoveTargetText(
                [targetMatter?.matterRef, targetMatter?.details]
                    .filter(Boolean)
                    .join(" - "),
            );
            setMoveSource(
                mode === "move"
                    ? { matterId: sourceMatterId, activity: sourceActivity }
                    : null,
            );
            props.setAddActivity(true);
            setViewActivity(mergeDuplicateActivityData(data?.data, sourceRow));
            setView(true);
            setIsDuplicate(true);
            setMoveCopyModal(null);
        } catch (error) {
        } finally {
            setMoveCopyProcessing(false);
        }
    };

    const handleAfterAddSave = async () => {
        if (!moveSource) return;
        const { matterId, activity } = moveSource;
        try {
            await updateMatterActivity(matterId, activity.activityId, {
                ...activity,
                active: false,
                details: `[Move] ${activity.details ?? ""}`,
            });
        } catch (error) {
            toast.error(
                "Activity copied, but the original could not be marked as moved.",
            );
        }
    };

    const resetMoveCopyState = () => {
        setCopyMoveTargetId(null);
        setCopyMoveTargetText(null);
        setMoveSource(null);
    };

    const getActivitiesData = async () => {
        setIsRefreshed(!isRefreshed);
    };

    const handleAttachToChit = (docs) => {
        if (!docs?.length) return;
        setPrefilledChitDocs(docs);
        setSelectedActivity(null);
        setChitRequestModal(true);
    };

    const handleActivityRowClick = async (activity) => {
        const matterId = props?.matterId ?? activity?.matterId;
        const activityId = activity?.activityId;

        const fetchLatestActivity = async () => {
            if (!matterId || !activityId) return activity;
            try {
                const { data } = await getSingleMatterActivity(matterId, activityId);
                return data?.data ?? data ?? activity;
            } catch (error) {
                toast.error("Could not load the latest activity details.");
                return activity;
            }
        };

        // Check if this activity should open ChitRequest modal
        if (activity.subType === "ChitRequest") {
            const latestActivity = await fetchLatestActivity();
            const chitId =
                latestActivity?.chitRequestId ??
                latestActivity?.chitId ??
                latestActivity?.subTypeId;
            if (!chitId) {
                return;
            }
            setSelectedActivity(latestActivity);
            setChitRequestModal(true);
            return;
        }

        if (activity.subType === "BillRequest") {
            const latestActivity = await fetchLatestActivity();
            const billRequestId = latestActivity?.subTypeId;
            if (!billRequestId) {
                return;
            }
            try {
                const response = await getBillingRequestDetails(billRequestId);
                const detailData = response.data?.data || {};
                const templateFromDetail = detailData.billTemplateResourceId;
                const payload = {
                    ...latestActivity,
                    ...detailData,
                    matterRef:
                        detailData.matterRef ||
                        latestActivity.matterRef ||
                        props.matterDetails?.matterRef ||
                        "",
                };
                if (isNonEmptyBillResourceId(templateFromDetail)) {
                    payload.billTemplateResourceId = templateFromDetail;
                }
                setBillingRequestPayload(payload);
                setBillRequestModal(true);
            } catch (error) { }
            return;
        }

        const latestActivity = await fetchLatestActivity();
        setSelectedState({
            [activity.activityId]: true,
        });
        props.setAddActivity(true);
        setViewActivity(latestActivity);
        setView(true);
    };

    const handleRefresh = () => {
        if (eventDataState) {
            setDataState({
                ...eventDataState?.event,
                skip: 0,
            });
        } else {
            setDataState({
                ...dataState,
                take: 50,
                skip: 0,
            });
        }
        setIsRefreshed(!isRefreshed);
        setUpdateFilter(true);
    };

    const handleReset = () => {
        filterAutoApplySkip.current = true;
        setUpdateFilter(true);
        setValueFrom(false);
        setValueTo(false);
        setFromDateSelector(yearsStartDate);
        setToDateSelector(date);
        setSelectedTypes([]);
        setSelectedTags([]);
        props.setFilterApplied(false);
        props.setFilterClicked(false);
    };

    const showDocumentFile = async (documentId, documentName) => {

        if (defaultDocumentClient === "LinqDesktopConnector"
            && !isOfficeFile(documentName) && !isEmailFile(documentName) ) {

            try {
                const { ticket } = await getAuthTicket();

                if (!ticket) {
                    toast.error("Unable to retrieve auth ticket.");
                    return;
                }


                document.documentElement.classList.add("wait-cursor");

                const iframe = document.createElement("iframe");
                iframe.style.display = "none";
                iframe.src = `linqlegal://open-document/${documentId}?ticket=${encodeURIComponent(ticket)}`;
                document.body.appendChild(iframe);

                setTimeout(() => {
                    iframe.remove();
                    document.documentElement.classList.remove("wait-cursor");
                }, 500);

                return;
            } catch (err) {
                console.error("Failed to retrieve auth ticket", err);
                document.documentElement.classList.remove("wait-cursor");
            }
        }

        if (isEmailFile(documentName)) {

            switch (defaultEmailClient) {

                case "OfficeWebMail":

                    try {
                        const { data } = await getOutlookWebURLViewEmail(documentId);
                        const url = data?.data?.outlookWebUrl;
                        if (url) {
                            window.open(url, "_blank", "noopener,noreferrer,width=800,height=800,left=200,top=100");
                            return;
                        }
                    } catch (err) {
                        console.error("Failed to open Outlook Web email", err);
                    }

                    break;

                case "LinqDesktopConnector":
                    try {
                        const { ticket } = await getAuthTicket();

                        if (!ticket) {
                            toast.error("Unable to retrieve auth ticket.");
                            break;
                        }


                        document.documentElement.classList.add("wait-cursor");

                        const iframe = document.createElement("iframe");
                        iframe.style.display = "none";
                        iframe.src = `linqlegal://open-email/${documentId}?ticket=${encodeURIComponent(ticket)}`;
                        document.body.appendChild(iframe);

                        setTimeout(() => {
                            iframe.remove();
                            document.documentElement.classList.remove("wait-cursor");
                        }, 500);

                        return;
                    } catch (err) {
                        console.error("Failed to retrieve auth ticket", err);
                        document.documentElement.classList.remove("wait-cursor");
                    }

                    break;

                default:
                    break;
            }

        }



        const matterRef = props.addFormText?.split("-")[0]?.trim();
        const encodedId = btoa(
            JSON.stringify({
                id: documentId,
                matterRef,
                matterId: props?.matterId,
            }),
        );

        window.open(`/sa/document-viewer?id=${encodedId}`, "_blank");
    };

    const openEditDocumentURL = (editUrl, id) => {
        if (!editUrl) {
            toast.error("Unable to retrieve the document editing URL.");
            return false;
        }

        window.open(editUrl, "_blank");
        showPaneFile(id);

        return true;
    };

    const getDocumentEditorData = async (id, type) => {
        setLoader(true);
        try {
            const isLocalEditing = type === "Local";

            if (!isLocalEditing) {
                const response = await getDocumentEditor(id, props?.matterId);
                const editUrl = response?.data?.data?.editURL;
                openEditDocumentURL(editUrl, id);
                return;
            }

            if (defaultEditOfficeDocumentClient === "LinqDesktopConnector") {

                const { ticket } = await getAuthTicket();

                if (!ticket) {
                    toast.error("Unable to retrieve auth ticket.");
                    return;
                }

                let editUrl = `linqlegal://edit-office-document/${id}?ticket=${encodeURIComponent(ticket)}&activityId=${id}&matterId=${props?.matterId}`;
                openEditDocumentURL(editUrl, id);
                return;
            }


            const response = await getDocumentEditor(id, props?.matterId);
            const documentEditUrl = response?.data?.data?.editURL;
            const document = paneDocument?.[0];

            const extension = document?.documentName
                ? getFileExtension(document.documentName).toLowerCase()
                : "";

            const editUrl = excelExt.includes(extension)
                ? `ms-excel:ofe|u|${documentEditUrl}`
                : `ms-word:ofe|u|${documentEditUrl}`;

            openEditDocumentURL(editUrl, id);

        } catch (error) {
            const statusCode = error?.response?.status;
            console.error("Error fetching document editor data:", error);

            // 460 is already handled globally
            if (statusCode !== 460) {
            }
        } finally {
            setLoader(false);
        }
    };

    const showPaneFile = async (id) => {
        setLoaded(false);
        try {
            if (!id) {
                setPaneDocument([]);
                setPanes(getDynamicDocOpenPanes());
                return;
            }
            const { data } = await getDocument(id);
            setPaneDocument([data.data]);
            setPanes(getDynamicDocOpenPanes());
        } catch (error) {
            setPaneDocument([]);
        } finally {
            setLoaded(true);
        }
    };

    const DocumentCell = (e) => {
        const { documentId, documentName } = e.dataItem;

        const handleDocDragStart = (event) => {
            handleDragStart(event, e.dataItem);
        };

        const hybridClick = useDoubleClick(
            () => showDocumentFile(documentId, documentName),
            () => {
                setSelectedState({
                    [e.dataItem.activityId]: true,
                });
                showPaneFile(documentId);
            },
        );

        return documentId ? (
            <td className={e.className ?? ""} style={e.style}>
                <div
                    className="d-flex"
                    draggable={true}
                    onDragStart={handleDocDragStart}
                    onClick={hybridClick}
                    style={{ cursor: "grab" }}
                    onMouseDown={(ev) => {
                        ev.currentTarget.style.cursor = "grabbing";
                    }}
                    onMouseUp={(ev) => {
                        ev.currentTarget.style.cursor = "grab";
                    }}>
                    {fileIcon(documentName)}

                    <span className="ps-2 fs-sm d-lg-none d-xl-block">
                        {truncateFileName(documentName)}
                    </span>

                    <span className="ps-2 fs-sm d-xl-none d-lg-block">
                        {documentName?.split(".").pop()}
                    </span>
                </div>
            </td>
        ) : (
            <td />
        );
    };

    const EmailCell = (props) => {
        const { emailFrom, emailTo, documentId, activityId } = props.dataItem;

        return (
            <td
                className={`fs-sm wrap-text ${props.className}`}
                onClick={(e) => {
                    e.stopPropagation();
                    setSelectedState({
                        [activityId]: true,
                    });
                    showPaneFile(documentId);
                }}>
                <div className="text-nowrap">{emailFrom}</div>
                <div className="text-nowrap">{emailTo}</div>
            </td>
        );
    };

    detailsCellRef.current = {
        setSelectedState,
        showPaneFile,
        loaded,
        setLoaded,
        selectedState,
        multiSel,
        setMultiSel,
        setAddActivity: props.setAddActivity,
        setViewActivity,
        setView,
        handleActivityRowClick,
        showDocumentFile,
        handleDownload,
        getDocumentEditorData,
        handleDuplicate,
        handleMoveCopyActivity,
        setShowShareDocument,
        setCurrentDoc,
        handleDeleteClick,
        setESigning,
        matterId: props?.matterId,
        activities: list?.data,
        handleAttachToChit,
        panes,
        openMenuActivityId,
        setOpenMenuActivityId,
    };

    const DetailsCell = useMemo(() => (cellProps) => {
        const activity = cellProps.dataItem;
        const status = getActivitySubtypeStatus(activity);
        const r = detailsCellRef.current;
        return (
            <td
                onClick={(e) => {
                    e.stopPropagation();
                    r.setSelectedState({ [activity.activityId]: true });
                    r.showPaneFile(activity?.documentId);
                }}
                className="pe-1 wrap-text cursor-default"
            >
                <div className="d-flex align-items-center justify-content-between gap-2 w-100">
                    <div className="flex-grow-1" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {status && (
                            <span className={`badge me-2 ${getStatusClass(status)}`} style={{ padding: "0.50em 0.75em 0.35em" }}>
                                {status}
                            </span>
                        )}
                        {activity?.details}
                    </div>
                    {activity?.tags?.length > 0 && (
                        <div className="d-flex gap-1 flex-shrink-0">
                            {activity.tags.map((tag) => (
                                <span
                                    key={getActivityTagId(tag)}
                                    title={tag.name}
                                    style={getActivityTagChipStyle(tag)}
                                >
                                    {tag.name}
                                </span>
                            ))}
                        </div>
                    )}
                    <MenuCell
                        noTd
                        dataItem={activity}
                        loaded={r.loaded}
                        setLoaded={r.setLoaded}
                        setSelectedState={r.setSelectedState}
                        selectedState={r.selectedState}
                        showPaneFile={r.showPaneFile}
                        multiSel={r.multiSel}
                        setMultiSel={r.setMultiSel}
                        setAddActivity={r.setAddActivity}
                        setViewActivity={r.setViewActivity}
                        setView={r.setView}
                        handleActivityRowClick={r.handleActivityRowClick}
                        showDocumentFile={r.showDocumentFile}
                        handleDownload={r.handleDownload}
                        getDocumentEditorData={r.getDocumentEditorData}
                        handleDuplicate={r.handleDuplicate}
                        handleMoveCopyActivity={r.handleMoveCopyActivity}
                        setShowShareDocument={r.setShowShareDocument}
                        setCurrentDoc={r.setCurrentDoc}
                        onDeleteClick={r.handleDeleteClick}
                        setESigning={r.setESigning}
                        matterId={r.matterId}
                        activities={r.activities}
                        onAttachToChit={r.handleAttachToChit}
                        isDocViewerOpen={Boolean(r.panes[1]?.size)}
                        openMenuActivityId={r.openMenuActivityId}
                        setOpenMenuActivityId={r.setOpenMenuActivityId}
                    />
                </div>
            </td>
        );
    }, []);

    const saveDocuemntEdit = async (id) => {
        try {
            await saveDocumentEditor(id, props.matterId);
            toast.success("Document Saved");
            showPaneFile(id);
        } catch (error) { } finally {
            // setLoadingScreen(false);
        }
    };

    const DateCell = (props) => {
        const dateField = props.dataItem[props.field];

        if (dateField) {
            const dt = new Date(dateField);
            const dateStr = dt.toLocaleDateString(locale || "en-GB");
            const timeStr = dt.toLocaleTimeString(locale || "en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }); // e.g. 14:27

            return (
                <td
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedState({
                            [props.dataItem.activityId]: true,
                        });
                        showPaneFile(props.dataItem?.documentId);
                    }}
                    className={`fs-sm ${props.className}`}>
                    <div className="text-nowrap">{dateStr}</div>
                    <div className="text-nowrap">{timeStr}</div>
                </td>
            );
        } else {
            return <td className={props.className}></td>;
        }
    };

    const dataStateChange = (e) => {
        const nextSort = e.dataState.sort;
        const prevSortField = dataState.sort?.[0]?.field ?? "date";

        if (
            nextSort !== undefined &&
            e.dataState.skip !== 0 &&
            dataState.sort !== nextSort
        ) {
            setDataState({ ...e.dataState, skip: 0 });
        } else if (nextSort && nextSort[0] === undefined) {
            setDataState({
                ...e.dataState,
                sort: [
                    {
                        field: prevSortField,
                        dir: "asc",
                    },
                ],
            });
        } else {
            setDataState(e.dataState);
        }
        setUpdateFilter(true);
    };

    const TypeCell = (prop) => {
        const handleTypeClick = (e) => {
            e.stopPropagation();
            handleActivityRowClick(prop.dataItem);
        };

        return (
            <td className="fs-sm wrap-text">
                <u style={{ cursor: "pointer" }} onClick={handleTypeClick}>
                    {prop.dataItem.type}
                </u>
            </td>
        );
    };

    const dataReceived = (data) => {
        if (!data) return;
        setLists(data);
        setLoader(false);

        if (props.searchValue && data.data?.length > 0) {
            const firstRow = data.data[0];
            if (firstRow?.activityId) {
                setSelectedState({
                    [firstRow.activityId]: true,
                });

                showPaneFile(firstRow.documentId ?? null);
            }
        }
    };

    const handleDragStart = (e, dataItem) => {
        applyActivitiesDragTransfer(e, dataItem, {
            listData: list?.data,
            selectedState,
            idGetter,
            defaultMatterId: props.matterId,
        });
    };

    const mappedData = buildMappedGridData(list, selectedState, idGetter);

    const hasFiles = useCallback((e) => {
        const types = e?.dataTransfer?.types;
        if (!types) return false;
        return typeof types.contains === "function"
            ? types.contains("Files")
            : Array.from(types).includes("Files");
    }, []);

    const onPageDragEnter = useCallback(
        (e) => {
            if (hasFiles(e)) setIsDraggingFile(true);
        },
        [hasFiles],
    );

    const onPageDragLeave = useCallback((e) => {
        const related = e.relatedTarget;
        if (related == null || !activityGridRef.current?.contains(related)) {
            setIsDraggingFile(false);
            setIsOverGrid(false);
        }
    }, []);

    const onPageDragOver = useCallback(
        (e) => {
            if (hasFiles(e)) e.preventDefault();
        },
        [hasFiles],
    );

    const onPageDrop = useCallback(() => {
        setIsDraggingFile(false);
        setIsOverGrid(false);
    }, []);

    return (
        <div
            ref={activityGridRef}
            className="activity-grid h-100"
            onDragEnter={onPageDragEnter}
            onDragLeave={onPageDragLeave}
            onDragOver={onPageDragOver}
            onDrop={onPageDrop}>
            <Splitter
                panes={panes}
                onChange={onChange}
                onBarToggle={() => {
                    if (panes[0].collapsed) {
                        setPanes(getDynamicDocOpenPanes());
                    } else {
                        setPanes([
                            {
                                collapsed: true,
                            },
                            {},
                        ]);
                    }
                }}
                className={"workflow-splitter border-0 h-100"}>
                <FileDragDrop
                    onFilesDrop={handleDrop}
                    uploadRef={uploadRef}
                    onOverGridChange={setIsOverGrid}
                    onDragEnd={() => {
                        setIsDraggingFile(false);
                        setIsOverGrid(false);
                    }}
                    isOverGrid={isOverGrid}
                    isDraggingFile={isDraggingFile}>
                    <div style={{ height: "100%" }}>
                        <Grid
                            key={columnsRenderKey}
                            {...dataState}
                            {...columnGridProps}
                            data={mappedData}
                            sortable={true}
                            dataItemKey={ACTIVITY_DATA_ITEM_KEY}
                            selectedField={ACTIVITY_SELECTED_FIELD}
                            selectable={{
                                enabled: true,
                                drag: false,
                                cell: false,
                                mode: "multiple",
                            }}
                            onDataStateChange={dataStateChange}
                            onSelectionChange={onSelectionChange}
                            onKeyDown={onKeyDown}
                            className="h-100 overflow-auto grid-row-select casefile_outlook_grid"
                            pageable={state.pageable}
                            pageSize={state.pageSize}
                            rowRender={(row, props) => {
                                const style = getRowStyle(props.dataItem, activityTypes);
                                return React.cloneElement(row, {
                                    style: {
                                        ...row.props.style,
                                        ...style,
                                    },
                                });
                            }}>
                            <GridNoRecords>
                                {loader ? (
                                    <div style={{ height: "100px" }}></div>
                                ) : (
                                    mappedData.data &&
                                    mappedData.data.length === 0 &&
                                    "No records available"
                                )}
                            </GridNoRecords>
                            <GridColumn
                                field={ACTIVITY_SELECTED_FIELD}
                                width={50}
                                headerCell={columnsHeaderCell}
                                reorderable={false}
                                resizable={false}
                            />
                            {visibleColumns.map((col) => {
                                const w = col.width ?? undefined;
                                switch (col.field) {
                                    case "type":
                                        return <GridColumn key="type" field="type" className="fs-sm" title="Type" cell={TypeCell} width={w} minResizableWidth={60} id="type" />;
                                    case "date":
                                        return <GridColumn key="date" field="date" title="Date" cell={DateCell} width={w} minResizableWidth={80} className="px-2 py-1 cursor-default" />;
                                    case "feeEarnerRef":
                                        return <GridColumn key="feeEarnerRef" field="feeEarnerRef" title="FE" width={w} minResizableWidth={40} className="cursor-default" />;
                                    case "details":
                                        return <GridColumn key="details" field="details" title="Details" cell={DetailsCell} width={w} minResizableWidth={180} className={`cursor-default ${activityStyles["details-col"]}`} />;
                                    case "document":
                                        return <GridColumn key="document" field="document" title="Document" cell={DocumentCell} width={w} minResizableWidth={80} className="fs-sm px-1" sortable={false} />;
                                    case "email":
                                        return <GridColumn key="email" field="email" title="Email" cell={EmailCell} width={w} minResizableWidth={100} className="fs-sm px-1 cursor-default" sortable={false} />;
                                    case "emailFrom":
                                        return <GridColumn key="emailFrom" field="emailFrom" title="Email From" width={w} minResizableWidth={100} className="fs-sm px-1 cursor-default" />;
                                    case "emailTo":
                                        return <GridColumn key="emailTo" field="emailTo" title="Email To" width={w} minResizableWidth={100} className="fs-sm px-1 cursor-default" />;
                                    default:
                                        return null;
                                }
                            })}
                            <GridColumn reorderable={false} resizable={false} width={1} headerCell={() => null} headerClassName="activities-spacer-col" className="activities-spacer-col" />
                        </Grid>
                        {loader && <GridLoader />}
                    </div>
                </FileDragDrop>

                <div className="h-100">
                    <DocumentViewer
                        loader={loaded}
                        matterId={props?.matterId}
                        matter={props?.matterDetails}
                        onDownload={handleDownload}
                        saveDocuemntEdit={saveDocuemntEdit}
                        data={paneDocument && paneDocument.length > 0 ? paneDocument : []}
                        onRefresh={(id) => showPaneFile(id)}
                        onScreenComponent={true}
                        onEditDoc={(e, type) => getDocumentEditorData(e, type)}
                        onClose={() => {
                            setPanes(DEFAULT_PANES);
                            setPaneDocument([]);
                            setSelectedState([]);
                        }}
                        onExpand={() => {
                            if (panes[0].collapsed) {
                                setPanes(getDynamicDocOpenPanes());
                            } else {
                                setPanes([
                                    {
                                        collapsed: true,
                                    },
                                    {},
                                ]);
                            }
                        }}
                        onOpenInNewTab={showDocumentFile}
                        component="caseActivities"
                    />
                </div>
            </Splitter>

            {isDraggingFile && !isOverGrid && (
                <div
                    className={activityStyles["drop-invalid-banner"]}
                    role="status"
                    aria-live="polite">
                    <MdOutlineBlock
                        className={activityStyles["drop-invalid-icon"]}
                        aria-hidden
                    />
                    <span>Drop files only on the activity grid to attach to the case.</span>
                </div>
            )}

            <Popup
                key={props?.filterClicked ? "open" : "closed"}
                show={props?.filterClicked}
                anchor={props?.filterAnchor?.current}
                anchorAlign={{
                    horizontal: "right",
                    vertical: "bottom",
                }}
                popupAlign={{
                    horizontal: "right",
                    vertical: "top",
                }}
                popupClass={`${styles["popup-content"]}`}
                onOpen={onOpen}>
                <div
                    ref={props?.filterContentRef}
                    role="menu"
                    className="d-flex flex-column align-items-center">
                    <FilterComponent
                        updateFilterValues={updateFilterValues}
                        setUpdateFilter={setUpdateFilter}
                        updateFilter={updateFilter}
                        autoApply={true}
                        showPopup={true}
                        valueFrom={valueFrom}
                        setValueFrom={setValueFrom}
                        fromDateSelector={fromDateSelector}
                        onChangeFromDateSelect={onChangeFromDateSelect}
                        valueTo={valueTo}
                        setValueTo={setValueTo}
                        toDateSelector={toDateSelector}
                        onChangeToDateSelect={onChangeToDateSelect}
                        typeData={activityTypes}
                        typeID={selectedTypes}
                        handleChange={handleTypeChange}
                        handleReset={handleReset}
                        tagsData={tagsData}
                        selectedTags={selectedTags}
                        onTagsChange={(e) => setSelectedTags(e.value)}
                    />
                </div>
            </Popup>

            <HandleGridBody
                matterId={props.matterId}
                feeEarner={filterFeeEarner}
                typeId={selectedTypeIds}
                tagIds={selectedTagIds}
                refresh={isRefreshed}
                updateFilter={updateFilter}
                fD={valueFrom ? fromDate : ""}
                tD={valueTo ? toDate : ""}
                dataState={dataState}
                onDataReceived={dataReceived}
                currentComponent="caseActivities"
                searchValue={props?.searchValue}
                showLoader={false}
            />

            {props.addActivity && (
                <CaseActivityForm
                    getActivitiesData={getActivitiesData}
                    activityTags={tagsData}
                    activityTagsLoading={tagsLoading}
                    activityTagsFetched={tagsFetched}
                    addFormText={copyMoveTargetText ?? props.addFormText}
                    droppedFilesActivity={droppedFilesActivity}
                    defaultTypeByFile={droppedTypesByFile}
                    lockedDate={droppedEmailDate || undefined}
                    lockedDatesByFile={droppedEmailDatesByFile}
                    emailSubjectsByFile={droppedEmailSubjectsByFile}
                    viewActivity={viewActivity}
                    feeEarnerRef={
                        isView
                            ? props.matterDetails?.feeEarnerRef
                            : (currentUser?.userRef ?? props.matterDetails?.feeEarnerRef)
                    }
                    matterDetails={props.matterDetails}
                    isView={isView}
                    isDuplicate={isDuplicate}
                    reference={copyMoveTargetId ?? props.matterId}
                    onAfterAddSave={handleAfterAddSave}
                    onMatterRefClickActions={onMatterRefClickActions}
                    setshowMatterForm={() => {
                        setViewActivity();
                        setView(false);
                        setDroppedFilesActivity([]);
                        setDroppedTypesByFile({});
                        setDroppedEmailDate(null);
                        setDroppedEmailDatesByFile({});
                        setDroppedEmailSubjectsByFile({});
                        if (isDuplicate) setIsDuplicate(false);
                        resetMoveCopyState();
                        props.setAddActivity(false);
                    }}
                    isAddActivityBtn={props.isAddActivityBtn}
                    onTagCreated={refreshMatterTags}
                />
            )}

            {moveCopyModal && (
                <MoveCopyActivityModal
                    sourceMatter={{
                        ...(props.matterDetails || {}),
                        matterId: props?.matterId ?? props.matterDetails?.matterId,
                    }}
                    activity={moveCopyModal.sourceActivity}
                    isProcessing={moveCopyProcessing}
                    onConfirm={handleMoveCopyConfirm}
                    onClose={() => setMoveCopyModal(null)}
                />
            )}

            {eSigning && (
                <ESigning
                    matterId={props?.matterId}
                    title={"Select Contacts"}
                    activityId={eSigning}
                    onClose={() => setESigning()}
                />
            )}

            {editDocumentDialog && (
                <DocumentEditor
                    documentId={editDocumentDialog}
                    cancelEdit={(isSucess) => {
                        setEditDocumentDialog();
                        if (isSucess) getActivitiesData();
                    }}
                    matterId={props.matterId}
                    onDownload={handleDownload}
                />
            )}

            {showShareDocument && (
                <ShareDocumentModal
                    onClose={() => setShowShareDocument(false)}
                    selectedDocuments={getSelectedDocumentsForShare(
                        selectedState,
                        list?.data,
                    )}
                    matterId={props.matterId}
                    matter={props.matterDetails}
                    currentDoc={currentDoc}
                />
            )}

            {deleteConfirmation && (
                <ConfirmationDialog
                    content={getDeleteConfirmationMessage(deleteConfirmation)}
                    onClickYes={() => {
                        deleteMatteractivity(deleteConfirmation);
                    }}
                    onClickNo={() => setDeleteConfirmation()}
                    onClose={() => setDeleteConfirmation()}
                />
            )}

            {emailAttachedPrompt && (
                <EmailAlreadyAttachedDialog
                    open
                    attachedItems={emailAttachedPrompt.attachedItems}
                    newMatterRef={props.matterDetails?.matterRef}
                    onClose={() => setEmailAttachedPrompt(null)}
                    onConfirm={() => {
                        const filesToAttach = emailAttachedPrompt.files;
                        const perFileTypes = emailAttachedPrompt.typesByFile;
                        const date = emailAttachedPrompt.emailDate;
                        const perFileDates = emailAttachedPrompt.datesByFile;
                        const perFileSubjects = emailAttachedPrompt.subjectsByFile;
                        setEmailAttachedPrompt(null);
                        proceedWithDroppedFiles(
                            filesToAttach,
                            perFileTypes,
                            date,
                            perFileDates,
                            perFileSubjects,
                        );
                    }}
                />
            )}

            {chitRequestModal && (selectedActivity || prefilledChitDocs.length > 0) && (
                <ChitRequest
                    cancelEdit={(modified, options) => {
                        setChitRequestModal(false);
                        setSelectedActivity(null);
                        setPrefilledChitDocs([]);
                        dispatch(clearMattersBalances("Chit Request"));
                        if (modified || options?.fullRefresh) handleRefresh();
                    }}
                    matter={props.matterDetails}
                    matterId={props.matterDetails?.matterId}
                    component={"modal"}
                    chitRequestId={
                        selectedActivity
                            ? (selectedActivity?.chitRequestId ??
                                selectedActivity?.chitId ??
                                selectedActivity?.subTypeId)
                            : undefined
                    }
                    unAllocated={Boolean(selectedActivity?.unallocatedOnly)}
                    viewMode="dialog"
                    activityData={selectedActivity || undefined}
                    prefilledDocuments={prefilledChitDocs}
                />
            )}

            {billRequestModal && billingRequestPayload && (
                <BillPostingModal
                    billingRequestPayload={billingRequestPayload}
                    onClose={() => {
                        setBillRequestModal(false);
                        setBillingRequestPayload(null);
                    }}
                    onPostSuccess={() => {
                        setBillRequestModal(false);
                        setBillingRequestPayload(null);
                        getActivitiesData();
                    }}
                />
            )}
        </div>
    );
};
export default CaseActivitiesPage;
